import { deepEqual } from 'fast-equals';

import { DUAL_BANNER_SLOTS } from 'components/hf/GlobalBanner/GlobalBanner.types';
import type {
  BannerLink,
  DualGlobalBannerData,
  GlobalBannerContentConfig,
  GlobalBannerSlotContent,
  PrimaryMedia
} from 'components/hf/GlobalBanner/GlobalBanner.types';

const normalizeLinkHref = (href?: string): string | undefined => {
  if (typeof href !== 'string' || !href.includes('?')) {
    return href;
  }

  const hashIndex = href.indexOf('#');
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const queryIndex = withoutHash.indexOf('?');
  if (queryIndex < 0) {
    return href;
  }

  const base = withoutHash.slice(0, queryIndex);
  const query = withoutHash.slice(queryIndex + 1);
  const parameters = query.split('&').filter(parameter => {
    const separatorIndex = parameter.indexOf('=');
    const encodedName = separatorIndex >= 0 ? parameter.slice(0, separatorIndex) : parameter;
    let name = encodedName;
    try {
      name = decodeURIComponent(encodedName.replace(/\+/g, ' '));
    } catch {
      // Keep malformed parameters render-significant instead of hiding a real URL change.
    }
    return !name.toLowerCase().startsWith('pf_rd_');
  });

  return `${base}${parameters.length ? `?${parameters.join('&')}` : ''}${hash}`;
};

const getCtaLinkRenderState = (link?: BannerLink) =>
  link && {
    gae: link.gae,
    href: normalizeLinkHref(link.href),
    text: link.text
  };

const getBackgroundLinkRenderState = (link?: BannerLink) =>
  link && {
    gae: link.gae,
    href: normalizeLinkHref(link.href)
  };

const getContentRenderState = (content?: GlobalBannerContentConfig) => {
  const contentCtaLinks = content?.ctalinks;
  const ctalinks = (Array.isArray(contentCtaLinks) ? contentCtaLinks : []).map(getCtaLinkRenderState);
  if (ctalinks.length === 0) {
    return undefined;
  }

  return {
    color: content?.color,
    ctaLayoutDirection: content?.ctaLayoutDirection ?? 'horizontal',
    ctaSpacing: content?.ctaSpacing ?? 'grouped',
    ctalinks,
    desktopPaddingX: content?.desktopPaddingX || 0,
    desktopPaddingY: content?.desktopPaddingY || 0,
    mobilePaddingX: content?.mobilePaddingX || 0,
    mobilePaddingY: content?.mobilePaddingY || 0,
    position: content?.position ?? 'inside-left-center',
    shouldFillWidth: content?.shouldFillWidth ?? false
  };
};

const getMediaRenderState = (media?: PrimaryMedia) => {
  if (!media) {
    return undefined;
  }

  const sources = Array.isArray(media.sources) ? media.sources : [];
  const desktopSource = sources.find(source => source && !source.media);

  return {
    alt: media.alt,
    height: desktopSource?.height,
    sources: sources.map(source =>
      source
        ? {
            media: source.media || undefined,
            srcset: source.srcset
          }
        : source
    ),
    src: media.src,
    width: desktopSource?.width
  };
};

const getBannerRenderState = (banner?: GlobalBannerSlotContent) =>
  banner && {
    componentName: banner.componentName,
    containerBreak: banner.containerBreak ?? false,
    content: getContentRenderState(banner.content),
    creativeID: banner.creativeID,
    link: getBackgroundLinkRenderState(banner.links?.[0]),
    media: getMediaRenderState(banner.media?.primary)
  };

const getDualBannerRenderState = (banners?: DualGlobalBannerData) => DUAL_BANNER_SLOTS.map(slot => getBannerRenderState(banners?.slotData?.[slot]));

export const getGlobalBannerRenderKey = (banner: GlobalBannerSlotContent): string => JSON.stringify(getBannerRenderState(banner));

export const areDualGlobalBannersRenderEquivalent = (left?: DualGlobalBannerData, right?: DualGlobalBannerData): boolean =>
  deepEqual(getDualBannerRenderState(left), getDualBannerRenderState(right));
